import { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../../utils/axiosInstance";
import { AuthContext } from "../../../context/AuthContext";
import { isFreelance, isProjectManager } from "../../../utils/constants/accountType";
import { PROJECT_STATUSES, projectStatusStyle } from "../../../utils/constants/projectStatus";
import { JOB_STATUSES, jobStatusStyle } from "../../../utils/constants/jobStatus";
import { QUOTATION_STATUSES, quotationStatusStyle } from "../../../utils/constants/quotationStatus";
import { quickActionsFor } from "../../../utils/quickActions";
import Panel from "../../../components/Dashboard/Panel";
import StatTile from "../../../components/Dashboard/StatTile";
import StatusBreakdown from "../../../components/Dashboard/StatusBreakdown";
import DataTable from "../../../components/Dashboard/DataTable";
import TrendBars from "../../../components/Dashboard/TrendBars";
import Badge from "../../../components/Dashboard/Badge";
import WelcomeBanner from "../../../components/Dashboard/WelcomeBanner";
import AttentionList from "../../../components/Dashboard/AttentionList";
import InvoiceSummary, { invoiceTotals } from "../../../components/Dashboard/InvoiceSummary";

import {
  countBy,
  daysUntil,
  displayNameOf,
  dueLabel,
  entityName,
  fmtAmount,
  fmtDate,
  greetingFor,
  monthlyCounts,
  scopeLabel,
} from "../../../components/Dashboard/dashboardKit";

import {
  Users,
  Briefcase,
  Folder,
  Layers,
  ClipboardList,
  Wallet,
  AlarmClock,
  CalendarClock,
  Receipt,
  Hourglass,
} from "lucide-react";

const SAMPLE = 100;

// Matches the statuses the deadline reminders treat as still pending.
const OPEN_PROJECT_STATUSES = ["Offered by Client", "Offer Accepted", "Draft", "In Progress", "Hold", "Submission Rejected"];
const OPEN_JOB_STATUSES = ["Offered to Vendor", "Offer Accepted", "Started", "Hold", "Completion Rejected"];
const DECIDED_QUOTE_STATUSES = ["Accepted", "Rejected", "Expired"];

const viewAll = (to) => (
  <Link to={to} className="text-xs font-semibold text-brand-600 hover:text-brand-700 hover:underline">
    View all
  </Link>
);

const DueChip = ({ days }) => {
  const tone =
    days < 0
      ? "bg-rose-50 text-rose-600"
      : days <= 2
      ? "bg-amber-50 text-amber-700"
      : "bg-gray-100 text-gray-600";
  return <Badge className={tone}>{dueLabel(days)}</Badge>;
};

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

const summaryLine = (dueThisWeek, overdue) => {
  if (!dueThisWeek && !overdue) return "No deadlines this week and nothing overdue. A good day to plan ahead.";
  const parts = [];
  if (dueThisWeek) parts.push(`${plural(dueThisWeek, "deadline")} in the next 7 days`);
  if (overdue) parts.push(`${plural(overdue, "item")} overdue`);
  return `You have ${parts.join(" and ")}.`;
};

const AdminDashboard = () => {
  const { user } = useContext(AuthContext);
  const freelance = isFreelance(user);
  const projectManager = isProjectManager(user);
  const showManagers = !freelance && !projectManager;
  const showClientPools = !freelance && !projectManager;
  const showVendors = !freelance;
  const showJobs = !freelance;
  const showQuotationTile = freelance;
  const showFinances = freelance;

  const [counts, setCounts] = useState({
    managers: null, clients: null, vendors: null, clientPools: null, projects: null, jobs: null, quotations: null, invoices: null,
  });
  const [projects, setProjects] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [quotations, setQuotations] = useState(null);
  const [invoices, setInvoices] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const countOf = (res) =>
      res?.data?.meta?.total ?? (Array.isArray(res?.data?.data) ? res.data.data.length : 0);

    const skip = Promise.resolve(null);
    const get = (url) =>
      api.get(url).catch((err) => {
        console.error(`Dashboard: ${url} failed`, err);
        return null;
      });

    const fetchAll = async () => {
      try {
        const [managersRes, clientsRes, vendorsRes, clientPoolsRes, projectsRes, jobsRes, quotationsRes, invoicesRes] =
          await Promise.all([
            showManagers ? get("/managers") : skip,
            get("/clients"),
            showVendors ? get("/vendors") : skip,
            showClientPools ? get("/client-pools") : skip,
            get(`/projects?limit=${SAMPLE}`),
            showJobs ? get(`/jobs?limit=${SAMPLE}`) : skip,
            get(`/quotations?limit=${SAMPLE}`),
            get(`/client-invoices?limit=${SAMPLE}`),
          ]);

        setCounts({
          managers:    managersRes ? countOf(managersRes) : null,
          clients:     clientsRes ? countOf(clientsRes) : null,
          vendors:     vendorsRes ? countOf(vendorsRes) : null,
          clientPools: clientPoolsRes ? countOf(clientPoolsRes) : null,
          projects:    projectsRes ? countOf(projectsRes) : null,
          jobs:        jobsRes ? countOf(jobsRes) : null,
          quotations:  quotationsRes ? countOf(quotationsRes) : null,
          invoices:    invoicesRes ? countOf(invoicesRes) : null,
        });

        setProjects(projectsRes?.data?.data ?? []);
        setJobs(jobsRes?.data?.data ?? []);
        // null when the role cannot see the module, so its panel stays hidden.
        setQuotations(quotationsRes ? quotationsRes.data?.data ?? [] : null);
        setInvoices(invoicesRes ? invoicesRes.data?.data ?? [] : null);
      } catch (err) {
        console.error("AdminDashboard fetch error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [showManagers, showClientPools, showVendors, showJobs]);

  const now = new Date();

  const openProjects = projects.filter((p) => p.deadline_at && OPEN_PROJECT_STATUSES.includes(p.status));
  const openJobs = jobs.filter((j) => j.deadline_at && OPEN_JOB_STATUSES.includes(j.status));

  const deadlineItems = [
    ...openProjects.map((p) => ({
      key: `p${p.id}`,
      kind: "Project",
      name: p.project_name,
      sub: entityName(p.client) ?? "No client",
      to: `/project/${p.id}`,
      at: p.deadline_at,
    })),
    ...openJobs.map((j) => ({
      key: `j${j.id}`,
      kind: "Job",
      name: j.name,
      sub: entityName(j.vendor) ?? "No vendor",
      to: j.project?.id ? `/project/${j.project.id}/job/${j.id}` : "/jobs",
      at: j.deadline_at,
    })),
  ].sort((a, b) => new Date(a.at) - new Date(b.at));

  const overdueProjects = openProjects.filter((p) => new Date(p.deadline_at) < now);
  const overdueJobs = openJobs.filter((j) => new Date(j.deadline_at) < now);
  const dueToday = deadlineItems.filter((d) => daysUntil(d.at) === 0 && new Date(d.at) >= now);
  const dueThisWeek = deadlineItems.filter((d) => {
    const days = daysUntil(d.at);
    return days >= 0 && days <= 6 && new Date(d.at) >= now;
  });

  const overdueInvoices = (invoices ?? []).filter(
    (i) => Number(i.due_amount) > 0 && i.due_at && new Date(i.due_at) < now
  );
  const overdueInvoiceHint = invoiceTotals(overdueInvoices)
    .map((row) => fmtAmount(row.overdue, { code: row.code }))
    .join(" + ");

  const expiringQuotes = (quotations ?? []).filter((q) => {
    if (q.status !== "Sent" || !q.expire_at) return false;
    const days = daysUntil(q.expire_at);
    return days >= 0 && days <= 7;
  });

  const attention = [
    {
      key: "overdue-projects",
      icon: AlarmClock,
      tone: "critical",
      label: "Overdue projects",
      hint: "Past their deadline and not yet submitted",
      count: overdueProjects.length,
      to: "/projects",
    },
    ...(showJobs
      ? [{
          key: "overdue-jobs",
          icon: AlarmClock,
          tone: "critical",
          label: "Overdue jobs",
          hint: "Past their deadline and not yet completed",
          count: overdueJobs.length,
          to: "/jobs",
        }]
      : []),
    {
      key: "overdue-invoices",
      icon: Receipt,
      tone: "critical",
      label: "Overdue client invoices",
      hint: overdueInvoiceHint ? `${overdueInvoiceHint} unpaid` : null,
      count: overdueInvoices.length,
      to: "/client/invoices",
    },
    {
      key: "due-today",
      icon: CalendarClock,
      tone: "warning",
      label: "Due today",
      hint: "Projects and jobs with a deadline later today",
      count: dueToday.length,
      to: "/projects",
    },
    {
      key: "expiring-quotes",
      icon: Hourglass,
      tone: "warning",
      label: "Quotations expiring soon",
      hint: "Sent and expiring within 7 days",
      count: expiringQuotes.length,
      to: "/quotations",
    },
  ];
  const overdueCount = overdueProjects.length + overdueJobs.length + overdueInvoices.length;

  const projectBreakdown = countBy(projects, "status", PROJECT_STATUSES).map(({ key, count }) => ({
    label: key,
    count,
    dot: projectStatusStyle(key).dot,
  }));

  const jobBreakdown = countBy(jobs, "status", JOB_STATUSES).map(({ key, count }) => ({
    label: key,
    count,
    dot: jobStatusStyle(key).dot,
  }));

  const quotationBreakdown = countBy(quotations ?? [], "status", QUOTATION_STATUSES).map(({ key, count }) => ({
    label: key,
    count,
    dot: quotationStatusStyle(key).dot,
  }));
  const decided = (quotations ?? []).filter((q) => DECIDED_QUOTE_STATUSES.includes(q.status));
  const accepted = decided.filter((q) => q.status === "Accepted").length;
  const acceptanceRate = decided.length ? Math.round((accepted / decided.length) * 100) : null;

  const monthlyProjects = monthlyCounts(projects, {
    truncated: counts.projects != null && projects.length < counts.projects,
  });

  const quickActions = (quickActionsFor(user)?.actions ?? []).slice(0, 4);

  return (
    <div className="space-y-5">
      <WelcomeBanner
        greeting={greetingFor(displayNameOf(user, "Admin"))}
        summary={summaryLine(dueThisWeek.length, overdueCount)}
        actions={quickActions}
        deadlines={deadlineItems.map((d) => d.at)}
        loading={loading}
      />

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-6">
        {showManagers && (
          <StatTile icon={Users} label="Managers" value={counts.managers} tone="brand" to="/managers" loading={loading} />
        )}
        <StatTile icon={Users} label="Clients" value={counts.clients} tone="violet" to="/clients" loading={loading} />
        {showVendors && (
          <StatTile icon={Users} label="Vendors" value={counts.vendors} tone="indigo" to="/vendors" loading={loading} />
        )}
        {showClientPools && (
          <StatTile icon={Layers} label="Client pools" value={counts.clientPools} tone="cyan" to="/client-pools" loading={loading} />
        )}
        <StatTile icon={Folder} label="Projects" value={counts.projects} tone="emerald" to="/projects" loading={loading} />
        {showQuotationTile && (
          <StatTile icon={ClipboardList} label="Quotations" value={counts.quotations} tone="cyan" to="/quotations" loading={loading} />
        )}
        {showFinances && (
          <StatTile icon={Wallet} label="Finances" hint="Receivables, invoices, payments" tone="amber" to="/finances" />
        )}
        {showJobs && (
          <StatTile icon={Briefcase} label="Jobs" value={counts.jobs} tone="amber" to="/jobs" loading={loading} />
        )}
      </div>

      <div className="grid gap-4 xl:grid-cols-3">
        <Panel title="Needs attention" subtitle="Overdue and time-sensitive items">
          <AttentionList items={attention} loading={loading} />
        </Panel>

        <Panel
          className="xl:col-span-2"
          title="Projects added"
          subtitle="Per month, over the last six months"
          action={viewAll("/projects")}
        >
          <TrendBars points={monthlyProjects} loading={loading} empty="No projects created yet." />
        </Panel>

        {invoices !== null && (
          <Panel
            title="Client invoices"
            subtitle={scopeLabel(invoices.length, counts.invoices, "invoices")}
            action={viewAll("/client/invoices")}
          >
            <InvoiceSummary totals={invoiceTotals(invoices)} loading={loading} />
          </Panel>
        )}

        <Panel
          title="Project pipeline"
          subtitle={scopeLabel(projects.length, counts.projects, "projects")}
          action={viewAll("/projects")}
        >
          <StatusBreakdown items={projectBreakdown} loading={loading} empty="No projects to break down yet." />
        </Panel>

        {quotations !== null && (
          <Panel
            title="Quotations"
            subtitle={
              acceptanceRate === null
                ? scopeLabel(quotations.length, counts.quotations, "quotations")
                : `${acceptanceRate}% accepted of ${plural(decided.length, "decided quote")}`
            }
            action={viewAll("/quotations")}
          >
            <StatusBreakdown items={quotationBreakdown} loading={loading} empty="No quotations yet." />
          </Panel>
        )}

        <Panel
          className="xl:col-span-2"
          title="Recent projects"
          subtitle="The five most recently created"
          action={viewAll("/projects")}
        >
          <DataTable
            loading={loading}
            rows={projects.slice(0, 5)}
            empty="No projects yet."
            columns={[
              {
                key: "name",
                header: "Project",
                cell: (p) => (
                  <Link
                    to={`/project/${p.id}`}
                    className="block max-w-[13rem] truncate text-sm font-medium text-gray-800 hover:text-brand-600"
                  >
                    {p.project_name}
                  </Link>
                ),
              },
              {
                key: "client",
                header: "Client",
                cell: (p) =>
                  p.client ? (
                    <Link
                      to={`/clients/${p.client.id}`}
                      className="block max-w-[10rem] truncate text-sm text-gray-500 hover:text-brand-600"
                    >
                      {entityName(p.client) ?? `CLT-${String(p.client.id).padStart(3, "0")}`}
                    </Link>
                  ) : (
                    <span className="text-sm text-gray-400">—</span>
                  ),
              },
              {
                key: "due",
                header: "Deadline",
                className: "hidden sm:table-cell",
                cell: (p) => <span className="text-sm text-gray-500">{fmtDate(p.deadline_at)}</span>,
              },
              {
                key: "status",
                header: "Status",
                cell: (p) => <Badge className={projectStatusStyle(p.status).badge}>{p.status}</Badge>,
              },
            ]}
          />
        </Panel>

        <Panel
          title="Upcoming deadlines"
          subtitle={showJobs ? "Open projects and jobs, soonest first" : "Open projects, soonest first"}
          action={viewAll("/projects")}
        >
          {loading ? (
            <div className="space-y-2">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-10 animate-pulse rounded-lg bg-gray-50" />
              ))}
            </div>
          ) : deadlineItems.length === 0 ? (
            <p className="py-2 text-sm text-gray-400">Nothing due on the open work.</p>
          ) : (
            <ul className="divide-y divide-gray-50">
              {deadlineItems.slice(0, 6).map((d) => (
                <li key={d.key} className="flex items-center justify-between gap-3 py-2.5 first:pt-0 last:pb-0">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      {showJobs && (
                        <span className="shrink-0 rounded bg-gray-100 px-1.5 py-px text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                          {d.kind}
                        </span>
                      )}
                      <Link to={d.to} className="truncate text-sm font-medium text-gray-800 hover:text-brand-600">
                        {d.name}
                      </Link>
                    </div>
                    <p className="mt-0.5 truncate text-xs text-gray-400">
                      {d.sub} · {fmtDate(d.at)}
                    </p>
                  </div>
                  <DueChip days={daysUntil(d.at)} />
                </li>
              ))}
            </ul>
          )}
        </Panel>

        {showJobs && (
          <>
            <Panel
              className="xl:col-span-2"
              title="Recent jobs"
              subtitle="The five most recently created"
              action={viewAll("/jobs")}
            >
              <DataTable
                loading={loading}
                rows={jobs.slice(0, 5)}
                empty="No jobs yet."
                columns={[
                  {
                    key: "name",
                    header: "Job",
                    cell: (j) =>
                      j.project?.id ? (
                        <Link
                          to={`/project/${j.project.id}/job/${j.id}`}
                          className="block max-w-[13rem] truncate text-sm font-medium text-gray-800 hover:text-brand-600"
                        >
                          {j.name}
                        </Link>
                      ) : (
                        <span className="block max-w-[13rem] truncate text-sm font-medium text-gray-800">
                          {j.name}
                        </span>
                      ),
                  },
                  {
                    key: "vendor",
                    header: "Vendor",
                    cell: (j) =>
                      j.vendor ? (
                        <Link
                          to={`/vendors/${j.vendor.id}`}
                          className="block max-w-[10rem] truncate text-sm text-gray-500 hover:text-brand-600"
                        >
                          {entityName(j.vendor) ?? `VEN-${String(j.vendor.id).padStart(3, "0")}`}
                        </Link>
                      ) : (
                        <span className="text-sm text-gray-400">—</span>
                      ),
                  },
                  {
                    key: "due",
                    header: "Deadline",
                    className: "hidden sm:table-cell",
                    cell: (j) => <span className="text-sm text-gray-500">{fmtDate(j.deadline_at)}</span>,
                  },
                  {
                    key: "status",
                    header: "Status",
                    cell: (j) => <Badge className={jobStatusStyle(j.status).badge}>{j.status}</Badge>,
                  },
                ]}
              />
            </Panel>

            <Panel
              title="Job status"
              subtitle={scopeLabel(jobs.length, counts.jobs, "jobs")}
              action={viewAll("/jobs")}
            >
              <StatusBreakdown items={jobBreakdown} loading={loading} empty="No jobs to break down yet." />
            </Panel>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;